import styled from "styled-components";
import { Button, Collapse, Input, InputNumber as AntInputNumber, Table, Checkbox, Select, DatePicker, DatePickerProps } from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import PhoneInput from "react-phone-input-2";
import dayjs from 'dayjs';

export const StepContainer = styled.div`
  padding: 0 24px 24px;
  @media (max-width: 768px) {
    padding: 0 16px 16px;
  }
`;

export const Title = styled.h2`
  font-size: 24px;
  color: #1b3160;
  margin-bottom: 24px;
`;

export const OptionContainer = styled.div<{ isOpen?: boolean }>`
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  margin-bottom: 16px;
  overflow: hidden;
`;

export const StyledCollapse = styled(Collapse)`
  background: white;
  border: none;
  margin-bottom: 24px;

  .ant-collapse-item {
    border: 1px solid #e6e6e6;
    border-radius: 4px !important;
    margin-bottom: 16px;
    overflow: hidden;

    &:last-child {
      border-bottom: 1px solid #e6e6e6 !important;
    }

    .ant-collapse-header {
      background: #f5f7ff;
      padding: 16px !important;
      align-items: center !important;
      color: #1b3160 !important;
      font-size: 20px;
      font-weight: bold;

      .ant-collapse-expand-icon {
        order: 2;
        margin-left: auto !important;
      }
    }

    .ant-collapse-content {
      border-top: 1px solid #e6e6e6;
      background: #f5f7ff;

      .ant-collapse-content-box {
        padding: 24px 16px;
        @media (max-width: 768px) {
          padding: 16px 12px;
        }
      }
    }
  }
`;

export const StyledPanel = styled(Collapse.Panel)`
  && {
    margin-bottom: 16px;
  }
`;

export const CollapseHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const DeleteButton = styled.span`
  color: #ff4d4f;
  font-size: 16px;
  cursor: pointer;
  padding: 4px 8px;
  margin-right: 16px;

  &:hover {
    color: #ff7875;
  }
`;

export const HeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 48px;
  color: #1b3160;
  font-size: 16px;
  font-weight: 500;
  flex: 1;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .option-number {
    min-width: 80px;
  }

  .date-info {
    min-width: 120px;
  }

  .contact-info {
    min-width: 120px;
  }

  .country-info {
    flex: 1;
  }
`;

export const AddOptionButton = styled(Button)`
  width: 100%;
  height: 48px;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export const RequiredMark = styled.span`
  color: #ff4d4f;
  margin-right: 4px;
`;

export const StyledPlusIcon = styled(PlusOutlined)`
  font-size: 16px;
  color: #1b3160;
`;

export const StyledMinusIcon = styled(MinusOutlined)`
  font-size: 16px;
  color: #1b3160;
`;

export const DimensionsContainer = styled.div`
  background: #f8f9fe;
  padding: 24px;
  border-radius: 4px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const MeasurementUnitGroup = styled.div`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 64px;

  .measurement-unit-form-item {
    margin-bottom: 0 !important;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

export const Space = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 48px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const UnitLabel = styled.div`
  font-size: 18px;
  color: #1b3160;
  font-weight: 600;
`;

export const DimensionGroup = styled.div`
  margin-bottom: 24px;
  position: relative;

  &:has(.ant-form-item-has-error) {
    .ant-form-item-explain {
      position: absolute;
      left: 0;
      bottom: -25px;
      font-size: 14px;
      color: #ff4d4f;
      display: flex;
      align-items: center;
      white-space: nowrap;

      &::before {
        content: "*";
        margin-right: 4px;
      }
    }
  }

  .dimension-form-item {
    margin-bottom: 0 !important;

    &.ant-form-item-has-error {
      .ant-input-number {
        border-color: #d9d9d9;
      }
    }
  }
`;

export const DimensionLabel = styled.div<{ required?: boolean; isNoMarginBottom?: boolean }>`
  font-size: 16px;
  color: #1b3160;
  margin-bottom: ${(props) => (props.isNoMarginBottom ? "0" : "8px")};
  font-weight: 600;

  ${(props) =>
    props.required &&
    `
        &::before {
            content: "*";
            color: #ff4d4f;
            margin-right: 4px;
        }
    `}
`;

export const DimensionInputGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  .ant-form-item-explain {
    display: none;
  }
`;

export const UnitSelect = styled.div`
  background: #1b3160;
  color: white;
  padding: 4px 8px;
  height: 24px;
  border-radius: 0 4px 4px 0;
  min-width: 24px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
`;

export const InputWithUnit = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

export const Note = styled.div`
  color: #ff4d4f;
  margin-bottom: 24px;
`;

export const NoteUl = styled.ul`
  color: #ff4d4f;
  margin: 0;
  padding-left: 16px;
`;

export const NoteLi = styled.li`
  color: #ff4d4f;
`;

export const WarningNote = styled.div`
  background: #fff9e6;
  border: 1px solid #ffe58f;
  padding: 16px;
  margin-top: 24px;
  border-radius: 4px;
`;

export const ContentTextArea = styled(Input.TextArea)`
  min-height: 120px;
`;

export const PhoneInputWrapper = styled.div`
  position: relative;
  .dropdown {
    position: absolute;
    z-index: 9999;
  }
`;

export const StyledPhoneInput = styled(PhoneInput)`
  .form-control {
    width: 100%;
    height: 40px;
    padding-left: 50px !important;
  }

  .react-tel-input {
    position: relative;
  }

  .country-list {
    position: fixed !important;
    margin-top: 2px !important;
    max-height: 200px;
    overflow-y: auto;
    background: white;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    width: 300px;
  }

  .selected-flag {
    position: absolute;
    z-index: 1;
    height: 100%;
    padding: 0 0 0 8px;
    border-radius: 3px 0 0 3px;
    border-right: 1px solid #d9d9d9;
  }

  .flag-dropdown {
    border: none;
    background: transparent;
  }
`;

export const DimensionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0px;
  margin-bottom: 24px;

  &.dimensionGridThree {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 0px;

    &.dimensionGridThree {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 0px;

    &.dimensionGridThree {
    grid-template-columns: repeat(3, 1fr);
  }
  }

  @media (max-width: 576px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0px;
  }
`;

export const PreMatchStyledTable = styled(Table)`
  .ant-table {
    font-size: 16px; // Increase base font size

    .ant-table-thead > tr > th {
      font-size: 16px;
      font-weight: 600;
      color: #1b3160;
      background: #f5f5f5;
      padding: 16px; // Increase padding for better readability
    }

    .ant-table-tbody > tr > td {
      font-size: 16px;
      padding: 16px;

      &.criteria-column {
        color: #1b3160;
        font-weight: 500;
      }
    }
  }

  .even-row {
    background-color: #fff;
  }

  .odd-row {
    background-color: #f5f5f5;
  }
`;

export const FormContainer = styled.div`
  padding: 0;
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  @media (max-width: 576px) {
    display: block;
    width: calc(100% - 32px);

    tbody {
      display: block;
    }

    tr {
      display: flex;
      flex-direction: column;
      border-bottom: 1px solid #e6e6e6;

      &:last-child {
        border-bottom: none;
      }
    }
  }
`;

export const TableRow = styled.tr`
  border-bottom: 1px solid #e6e6e6;

  &:last-child {
    border-bottom: none;
  }
`;

export const TableCell = styled.td<{ isEditable?: boolean }>`
  padding: 12px 16px;
  vertical-align: top;

  &:first-child {
    width: 250px;
    /* background: #f5f5f5; */
    color: #1b3160;
    font-weight: ${(props) => (props.isEditable ? "700" : "500")};

    @media (max-width: 576px) {
      width: 100%;
    }
  }

  .ant-form-item {
    margin-bottom: 0;
  }

  @media (max-width: 576px) {
    width: 100%;
  }
`;

export const ConfirmationContainer = styled.div`
  padding: 16px;
  /* border-top: 1px solid #e6e6e6; */

  @media (max-width: 576px) {
    padding: 12px;
  }

  .ant-checkbox-wrapper {
    display: flex;
    align-items: flex-start;

    span {
      padding-top: 3px;
    }
  }
`;

export const StyledCheckbox = styled(Checkbox)`
  .ant-checkbox-inner {
    border-radius: 50%;
  }

  .ant-checkbox + span {
    color: #ff4d4f;
    padding-left: 12px;
  }
`;

// Add new styled component for required star
export const RequiredStar = styled.span`
  color: #ff4d4f;
  margin-right: 4px;
`;

// Add new styled components for the price section
export const PriceHeader = styled(TableCell)`
  background: #134075 !important;
  color: white !important;
  font-weight: 500;
`;

export const PriceSubtext = styled.div`
  font-size: 14px;
  font-weight: normal;
  margin-top: 4px;
`;

export const ShipAddressContainer = styled.div`
  background: #ffffff;
  padding: 20px 20px 0 20px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    padding: 14px 14px 0 14px;
  }
`;

export const DimensionsTableContainer = styled.div`
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 24px;
`;

export const DimensionsTableHeader = styled.div`
  display: flex;
  align-items: center;
  background: #ffffff;
  padding: 12px 16px;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  h2 {
    color: #1b3160;
    font-size: 20px;
    margin: 0;
    white-space: nowrap;
  }
`;

export const DimensionsMeasurementUnitWrapper = styled.div`
  margin-left: auto;

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
  }

  .ant-space {
    @media (max-width: 480px) {
      display: flex;
      gap: 8px !important;
    }
  }
`;

export const DimensionsTableRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  background: #f5f5f5;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 576px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const DimensionsTableCell = styled.div<{ isLast?: boolean }>`
  padding: 12px 16px;
  display: flex;
  align-items: center;
  border-right: 1px solid #e6e6e6;
  background: #f5f5f5;

  @media (max-width: 1024px) {
    border-bottom: ${(props) => (props.isLast ? "none" : "1px solid #e6e6e6")};
  }

  &:last-child {
    border-right: none;
  }

  .label {
    color: #1b3160;
    margin-right: 8px;
    font-weight: 500;
  }

  .value {
    color: #000000;
    font-weight: 700;
  }
`;

// New styled components for ShipFromTable
export const DimensionsShipFromContainer = styled.div`
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 24px;
`;

export const DimensionsShipFromHeader = styled.div`
  display: flex;
  background: #ffffff;
  padding: 12px 16px;

  h2 {
    color: #1b3160;
    font-size: 20px;
    margin: 0;
  }
`;

export const DimensionsShipFromTable = styled.div`
  width: 100%;
  overflow-x: auto;

  table {
    width: 100%;
    min-width: 1000px; // Minimum width to prevent squishing
    border-collapse: collapse;
  }
`;

export const DimensionsShipFromTableHeader = styled.th`
  background: #f5f5f5;
  padding: 12px 16px;
  text-align: left;
  border-right: 1px solid #e6e6e6;
  color: #1b3160;
  font-weight: 500;
  font-size: 14px;

  &.sticky {
    position: sticky;
    left: 0;
    background: #f5f5f5;
    z-index: 1;
  }

  &:last-child {
    border-right: none;
  }
`;

export const DimensionsTableData = styled.td<{ isActive?: boolean }>`
  padding: 12px 16px;
  border-right: 1px solid #e6e6e6;
  border-top: 1px solid #e6e6e6;
  font-weight: ${(props) => (props.isActive ? "600" : "400")};
  white-space: nowrap;

  &.sticky {
    position: sticky;
    left: 0;
    background: #fff;
    z-index: 1;
  }

  &:last-child {
    border-right: none;
  }
`;

// Add new styled components
export const DimensionsGroup = styled.div`
  margin-bottom: 16px;
`;

export const DimensionsErrorMessage = styled.div`
  color: #ff4d4f;
  font-size: 14px;
  margin-top: 4px;
`;

export const DimensionsTableCellEditable = styled(DimensionsTableCell)`
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;

  .edit-row {
    display: flex;
    gap: 8px;
    width: 100%;
    margin-top: 4px;
  }

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-form-item-explain-error {
    font-size: 14px;
    color: #ff4d4f;
    margin-top: 4px;
  }
`;

// Add new styled components
export const DimensionsHeaderWithEdit = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }

  .edit-icon {
    color: #1890ff;
    cursor: pointer;
    font-size: 16px;

    &:hover {
      opacity: 0.8;
    }
  }
`;

export const DimensionsTableFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px;
  background: #fff;
  border-top: 1px solid #e6e6e6;
`;

export const CourierSelect = styled(Select)`
  width: 100%;
  background: #f8f9fe;
`;


export const PayByDate = styled(DatePicker) <DatePickerProps<dayjs.Dayjs>>`
  width: 100%;
  height: 32px;
  font-size: 14px !important;

 .ant-picker-input >input:placeholder-shown{
  font-size: 14px !important;
  }
`;

export const ShipFromRow = styled.tr<{ dimmed?: boolean, isHighlighted?: boolean }>`
  background-color: ${({ isHighlighted }) => (isHighlighted ? "#e6f7ff" : "transparent")};
  opacity: ${({ dimmed }) => (dimmed ? 0.4 : 1)};
`;

export const CourierLeftSideContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const CourierRightSideContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  div{
    min-width: 160px;
  }
`;
